import { Alert } from '@cbm/shared';
import { Check, X, Eye, MessageSquare, Download } from 'lucide-react';

interface AlertActionsProps {
  alert: Alert;
  onAcknowledge?: (alertId: string) => void;
  onClose?: (alertId: string) => void;
  onViewDetails?: (alertId: string) => void;
  onRequestExplanation?: (alertId: string) => void;
  onDownloadEvidence?: (alertId: string) => void;
  size?: 'xs' | 'sm' | 'md';
  showLabels?: boolean;
  className?: string;
}

const SIZE_STYLES = {
  xs: 'px-2 py-1 text-xs',
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-sm',
};

const ICON_SIZES = {
  xs: 'w-3 h-3',
  sm: 'w-4 h-4',
  md: 'w-4 h-4',
};

export function AlertActions({
  alert,
  onAcknowledge,
  onClose,
  onViewDetails,
  onRequestExplanation,
  onDownloadEvidence,
  size = 'sm',
  showLabels = true,
  className = ''
}: AlertActionsProps) {
  const canAcknowledge = alert.status === 'OPEN' && onAcknowledge;
  const canClose = alert.status === 'ACKNOWLEDGED' && onClose;
  const hasEvidence = alert.evidenceRefs?.length > 0;

  const buttonClass = `
    inline-flex items-center space-x-1 rounded border font-medium
    transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2
    ${SIZE_STYLES[size]}
  `;

  const iconSize = ICON_SIZES[size];

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      {/* Acknowledge button */}
      {canAcknowledge && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            console.log('🔘 AlertActions button clicked:', alert.alertId);
            onAcknowledge(alert.alertId);
          }}
          className={`
            ${buttonClass}
            border-yellow-300 text-yellow-700 bg-yellow-50
            hover:bg-yellow-100 focus:ring-yellow-500
          `}
        >
          <Check className={iconSize} />
          {showLabels && <span>Acknowledge</span>}
        </button>
      )}

      {/* Close button */}
      {canClose && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClose(alert.alertId);
          }}
          className={`
            ${buttonClass}
            border-green-300 text-green-700 bg-green-50
            hover:bg-green-100 focus:ring-green-500
          `}
        >
          <X className={iconSize} />
          {showLabels && <span>Close</span>}
        </button>
      )}

      {/* View details button */}
      {onViewDetails && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onViewDetails(alert.alertId);
          }}
          className={`
            ${buttonClass}
            border-blue-300 text-blue-700 bg-blue-50
            hover:bg-blue-100 focus:ring-blue-500
          `}
        >
          <Eye className={iconSize} />
          {showLabels && <span>Details</span>}
        </button>
      )}

      {/* Request explanation button */}
      {onRequestExplanation && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRequestExplanation(alert.alertId);
          }}
          className={`
            ${buttonClass}
            border-purple-300 text-purple-700 bg-purple-50
            hover:bg-purple-100 focus:ring-purple-500
          `}
        >
          <MessageSquare className={iconSize} />
          {showLabels && <span>Explain</span>}
        </button>
      )}

      {/* Download evidence button */}
      {hasEvidence && onDownloadEvidence && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDownloadEvidence(alert.alertId);
          }}
          className={`
            ${buttonClass}
            border-gray-300 text-gray-700 bg-gray-50
            hover:bg-gray-100 focus:ring-gray-500
          `}
        >
          <Download className={iconSize} />
          {showLabels && <span>Evidence</span>}
        </button>
      )}
    </div>
  );
}

// Quick action buttons for dashboard use
interface QuickAlertActionsProps {
  alert: Alert;
  onAcknowledge?: (alertId: string) => void;
  onClose?: (alertId: string) => void;
  className?: string;
}

export function QuickAlertActions({
  alert,
  onAcknowledge,
  onClose,
  className = ''
}: QuickAlertActionsProps) {
  if (alert.status === 'CLOSED') {
    return (
      <div className={`flex items-center space-x-1 ${className}`}>
        <span className="text-xs text-green-600 font-medium">Resolved</span>
      </div>
    );
  }

  return (
    <div className={`flex items-center space-x-1 ${className}`}>
      {alert.status === 'OPEN' && onAcknowledge && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onAcknowledge(alert.alertId);
          }}
          className="p-1 text-yellow-600 hover:text-yellow-800 hover:bg-yellow-50 rounded"
          title="Acknowledge"
        >
          <Check className="w-4 h-4" />
        </button>
      )}
      
      {alert.status === 'ACKNOWLEDGED' && onClose && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClose(alert.alertId);
          }}
          className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded"
          title="Mark as Done"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

// Bulk actions for multiple alerts
interface BulkAlertActionsProps {
  selectedAlerts: Alert[];
  onBulkAcknowledge?: (alertIds: string[]) => void;
  onBulkClose?: (alertIds: string[]) => void;
  onClearSelection?: () => void;
  className?: string;
}

export function BulkAlertActions({
  selectedAlerts,
  onBulkAcknowledge,
  onBulkClose,
  onClearSelection,
  className = ''
}: BulkAlertActionsProps) {
  const openAlerts = selectedAlerts.filter(alert => alert.status === 'OPEN');
  const acknowledgedAlerts = selectedAlerts.filter(alert => alert.status === 'ACKNOWLEDGED');

  if (selectedAlerts.length === 0) {
    return null;
  }

  return (
    <div className={`flex items-center space-x-3 p-3 bg-blue-50 border border-blue-200 rounded-lg ${className}`}>
      <span className="text-sm font-medium text-blue-900">
        {selectedAlerts.length} alert{selectedAlerts.length !== 1 ? 's' : ''} selected
      </span>
      
      <div className="flex items-center space-x-2">
        {openAlerts.length > 0 && onBulkAcknowledge && (
          <button
            onClick={() => onBulkAcknowledge(openAlerts.map(a => a.alertId))}
            className="px-3 py-1 text-sm font-medium text-yellow-700 bg-yellow-100 border border-yellow-300 rounded hover:bg-yellow-200"
          >
            Acknowledge {openAlerts.length}
          </button>
        )}
        
        {acknowledgedAlerts.length > 0 && onBulkClose && (
          <button
            onClick={() => onBulkClose(acknowledgedAlerts.map(a => a.alertId))}
            className="px-3 py-1 text-sm font-medium text-green-700 bg-green-100 border border-green-300 rounded hover:bg-green-200"
          >
            Close {acknowledgedAlerts.length}
          </button>
        )}
        
        {onClearSelection && (
          <button
            onClick={onClearSelection}
            className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
}